import React, { useCallback, useState } from 'react';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  orderBulkUploadService,
  OrderImportPreviewRow,
} from '../../services/orderBulkUploadService';

interface BulkOrderUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
}

const BulkOrderUploadModal: React.FC<BulkOrderUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [preview, setPreview] = useState<OrderImportPreviewRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setStep('upload');
    setPreview([]);
    setSelected(new Set());
    setError('');
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await orderBulkUploadService.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'order-import-template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || 'Failed to download template');
    }
  };

  const handleFile = async (uploaded: File) => {
    try {
      setLoading(true);
      setError('');
      const result = await orderBulkUploadService.parse(uploaded);
      setPreview(result.rows);
      const validIndexes = result.rows.filter((r) => r.isValid).map((r) => r.rowIndex);
      setSelected(new Set(validIndexes));
      setStep('preview');
    } catch (e: any) {
      setError(e.message || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (rowIndex: number, isValid: boolean) => {
    if (!isValid) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const indices = Array.from(selected);
      const result = await orderBulkUploadService.save(preview, indices);
      const msg = result.message || `Imported ${result.saved} order(s)`;
      if (result.errors?.length) {
        setError(`${msg}\n${result.errors.join('\n')}`);
      }
      onSuccess(msg);
      handleClose();
    } catch (e: any) {
      setError(e.message || 'Failed to import orders');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const validCount = preview.filter((r) => r.isValid).length;

  return (
    <>
      <style>{`
        .order-import-overlay {
          position: fixed; inset: 0; background: rgba(15,23,42,0.55);
          z-index: 1400; display: flex; align-items: center; justify-content: center;
          padding: 24px;
        }
        .order-import-modal {
          background: #fff; border-radius: 14px; width: min(980px, 100%);
          max-height: 92vh; display: flex; flex-direction: column;
          box-shadow: 0 24px 60px rgba(15,23,42,0.25);
          overflow: hidden;
        }
        .order-import-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 24px; border-bottom: 1px solid #e2e8f0;
          background: linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%);
        }
        .order-import-header h2 {
          margin: 0; font-size: 18px; font-weight: 700; color: #0f172a;
          display: flex; align-items: center; gap: 10px;
        }
        .order-import-header .close-btn {
          border: none; background: rgba(15,23,42,0.04); cursor: pointer;
          width: 34px; height: 34px; border-radius: 8px;
          display: inline-flex; align-items: center; justify-content: center;
          color: #475569;
        }
        .order-import-header .close-btn:hover { background: rgba(15,23,42,0.08); }
        .order-import-body {
          padding: 24px; overflow: auto; flex: 1; background: #fff;
        }
        .order-import-footer {
          padding: 14px 24px; border-top: 1px solid #e2e8f0;
          display: flex; justify-content: flex-end; gap: 10px; background: #fafbfc;
        }
        .order-import-error {
          background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c;
          padding: 12px 16px; border-radius: 10px; margin-bottom: 16px;
          font-size: 13px; white-space: pre-wrap;
        }
        .order-import-helper {
          font-size: 13px; color: #475569; margin: 0 0 16px 0; line-height: 1.55;
        }
        .order-import-actionbar {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap; margin-bottom: 16px;
          padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0;
          border-radius: 10px;
        }
        .order-import-actionbar .left { font-size: 13px; color: #334155; }
        .order-import-drop {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          border: 2px dashed #c7d2fe; border-radius: 14px;
          padding: 56px 24px; text-align: center; background: #f8fafc;
          cursor: pointer; transition: all .15s ease; min-height: 220px;
        }
        .order-import-drop:hover { background: #eff6ff; border-color: #6366f1; }
        .order-import-drop .icon-wrap {
          width: 64px; height: 64px; border-radius: 50%;
          background: #e0e7ff; color: #4f46e5;
          display: inline-flex; align-items: center; justify-content: center;
          margin-bottom: 14px;
        }
        .order-import-drop .title { font-weight: 700; font-size: 15px; color: #1e293b; }
        .order-import-drop .subtitle { font-size: 12px; color: #64748b; margin-top: 6px; }
        .order-import-required {
          margin-top: 18px; padding: 14px 16px; background: #fffbeb;
          border: 1px solid #fde68a; border-radius: 10px; font-size: 12.5px; color: #92400e;
        }
        .order-import-required b { color: #78350f; }
        .order-import-required code {
          background: #fef3c7; padding: 1px 6px; border-radius: 4px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px;
        }
        .order-import-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .order-import-table th, .order-import-table td {
          border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left;
        }
        .order-import-table th { background: #f8fafc; font-weight: 600; }
        .order-import-badge-err {
          display: inline-block; background: #fef2f2; color: #b91c1c;
          padding: 4px 8px; border-radius: 4px; font-size: 11.5px; line-height: 1.4;
          max-width: 320px; white-space: normal;
        }
        .order-import-badge-err b { font-weight: 700; }
        .order-import-badge-ok {
          display: inline-block; background: #ecfdf5; color: #047857;
          padding: 2px 6px; border-radius: 4px; font-size: 11px;
        }
        .btn-order-import {
          padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
          cursor: pointer; border: none; font-family: inherit;
          display: inline-flex; align-items: center; gap: 6px; line-height: 1;
        }
        .btn-order-import-primary { background: #4f46e5; color: #fff; }
        .btn-order-import-primary:hover { background: #4338ca; }
        .btn-order-import-secondary { background: #fff; color: #334155; border: 1px solid #cbd5e1; }
        .btn-order-import-secondary:hover { background: #f1f5f9; }
        .btn-order-import:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>
      <div className="order-import-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
        <div className="order-import-modal" onClick={(e) => e.stopPropagation()}>
          <div className="order-import-header">
            <h2>
              <UploadIcon sx={{ fontSize: 22, color: '#4f46e5' }} />
              Import Orders from Sheet
            </h2>
            <button type="button" onClick={handleClose} aria-label="Close" className="close-btn">
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
          </div>
          <div className="order-import-body">
            {error && <div className="order-import-error">{error}</div>}
            {step === 'upload' && (
              <>
                <p className="order-import-helper">
                  Upload a CSV or Excel file. Each row will be matched against your existing
                  clients (by email), projects (by name within the client), guest blog sites
                  (by domain — <code>https://</code> and <code>www</code> are ignored), and
                  publishers (by email). Matched rows become <b>ACTIVE</b> guest blog orders —
                  no cart, no payment required.
                </p>

                <div className="order-import-actionbar">
                  <div className="left">
                    Need the format? Download the template and fill in your rows.
                  </div>
                  <button
                    type="button"
                    className="btn-order-import btn-order-import-secondary"
                    onClick={handleDownloadTemplate}
                  >
                    <DownloadIcon sx={{ fontSize: 18 }} />
                    Download template
                  </button>
                </div>

                <label className="order-import-drop">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleFile(f);
                    }}
                  />
                  <span className="icon-wrap">
                    <UploadIcon sx={{ fontSize: 32 }} />
                  </span>
                  <div className="title">
                    {loading ? 'Parsing your file…' : 'Drop file here or click to upload'}
                  </div>
                  <div className="subtitle">Supported formats: .csv, .xlsx, .xls</div>
                </label>

                <div className="order-import-required">
                  <b>Required columns:</b> <code>Client Email</code>, <code>Project Name</code>,
                  <code>Site URL</code>, <code>Content URL</code>. <br />
                  <b>Optional:</b> <code>Publisher Email</code>, <code>Submission Type</code>{' '}
                  (GBS / LI / BOTH — defaults to GBS), <code>Order Date</code>. <br />
                  <b>Pricing:</b> No price column needed — the price is taken automatically
                  from the matched guest blog site, the client's percentage markup, and any
                  client-specific override (same logic as add-to-cart). <br />
                  <b>Notes:</b> Wrap project names that contain commas in double quotes
                  (e.g. <code>&quot;myPub,LLC&quot;</code>). Site URL is matched by domain only
                  (e.g. <code>example.com</code> matches <code>https://www.example.com</code>).
                  <code>Order Date</code> can be left blank — today's date will be used
                  automatically. Accepted date formats: <code>YYYY-MM-DD</code> or{' '}
                  <code>DD-MM-YYYY</code>.
                </div>
              </>
            )}
            {step === 'preview' && (
              <>
                <p style={{ fontSize: 13, marginTop: 0 }}>
                  <span className="order-import-badge-ok">{validCount} valid</span>
                  <span className="order-import-badge-err">{preview.length - validCount} with errors</span>
                  {' — '}
                  {selected.size} selected for import
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table className="order-import-table">
                    <thead>
                      <tr>
                        <th />
                        <th>Row</th>
                        <th>Client</th>
                        <th>Project</th>
                        <th>Site</th>
                        <th>Publisher</th>
                        <th>Price</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row) => (
                        <tr key={row.rowIndex} style={{ opacity: row.isValid ? 1 : 0.65 }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selected.has(row.rowIndex)}
                              disabled={!row.isValid}
                              onChange={() => toggleRow(row.rowIndex, row.isValid)}
                            />
                          </td>
                          <td>{row.rowIndex}</td>
                          <td>{row.labels.clientName || row.labels.clientEmail || '—'}</td>
                          <td>{row.labels.projectName || '—'}</td>
                          <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.labels.siteUrl || '—'}
                          </td>
                          <td>{row.labels.publisherEmail || row.labels.publisherName || '—'}</td>
                          <td>
                            {row.resolved?.price != null
                              ? `$${Number(row.resolved.price).toFixed(2)}`
                              : '—'}
                          </td>
                          <td>
                            {row.isValid ? (
                              <span className="order-import-badge-ok">Ready</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {row.errors.map((err, i) => (
                                  <div key={i} className="order-import-badge-err" title={err.error}>
                                    <b>{err.field}:</b> {err.error}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <div className="order-import-footer">
            {step === 'preview' && (
              <button
                type="button"
                className="btn-order-import btn-order-import-secondary"
                onClick={() => {
                  setStep('upload');
                  setPreview([]);
                  setSelected(new Set());
                }}
                disabled={loading}
              >
                Back
              </button>
            )}
            <button type="button" className="btn-order-import btn-order-import-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            {step === 'preview' && (
              <button
                type="button"
                className="btn-order-import btn-order-import-primary"
                onClick={() => void handleSave()}
                disabled={loading || selected.size === 0}
              >
                {loading ? 'Importing…' : `Import ${selected.size} order(s)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkOrderUploadModal;
